import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import commonMessages, { layoutMessages, layoutMessages as toolbarMessages } from '@atlaskit/editor-common/messages';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LayoutOneColumnIcon from '@atlaskit/icon/core/layout-one-column';
import LayoutThreeColumnsIcon from '@atlaskit/icon/core/layout-three-columns';
import LayoutThreeColumnsSidebarsIcon from '@atlaskit/icon/core/layout-three-columns-sidebars';
import LayoutTwoColumnsIcon from '@atlaskit/icon/core/layout-two-columns';
import LayoutTwoColumnsSidebarLeftIcon from '@atlaskit/icon/core/layout-two-columns-sidebar-left';
import LayoutTwoColumnsSidebarRightIcon from '@atlaskit/icon/core/layout-two-columns-sidebar-right';
import EditorLayoutSingleIcon from '@atlaskit/icon/glyph/editor/layout-single';
import LayoutThreeEqualIcon from '@atlaskit/icon/glyph/editor/layout-three-equal';
import LayoutThreeWithSidebarsIcon from '@atlaskit/icon/glyph/editor/layout-three-with-sidebars';
import LayoutTwoEqualIcon from '@atlaskit/icon/glyph/editor/layout-two-equal';
import LayoutTwoLeftSidebarIcon from '@atlaskit/icon/glyph/editor/layout-two-left-sidebar';
import LayoutTwoRightSidebarIcon from '@atlaskit/icon/glyph/editor/layout-two-right-sidebar';
import RemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { deleteActiveLayoutNode, getPresetLayout, setPresetLayout } from '../pm-plugins/actions';
import { LayoutThreeWithLeftSidebarsIcon } from './icons/LayoutThreeWithLeftSidebars';
import { LayoutThreeWithRightSidebarsIcon } from './icons/LayoutThreeWithRightSidebars';
var LAYOUT_TYPES = [{
  id: 'editor.layout.twoEquals',
  type: 'two_equal',
  title: toolbarMessages.twoColumns,
  icon: LayoutTwoColumnsIcon,
  iconFallback: LayoutTwoEqualIcon
}, {
  id: 'editor.layout.threeEquals',
  type: 'three_equal',
  title: toolbarMessages.threeColumns,
  icon: LayoutThreeColumnsIcon,
  iconFallback: LayoutThreeEqualIcon
}];
var LAYOUT_TYPES_WITH_SINGLE_COL = [{
  id: 'editor.layout.singeLayout',
  type: 'single',
  title: toolbarMessages.singleColumn,
  icon: LayoutOneColumnIcon,
  iconFallback: EditorLayoutSingleIcon
}].concat(LAYOUT_TYPES);
var SIDEBAR_LAYOUT_TYPES = [{
  id: 'editor.layout.twoRightSidebar',
  type: 'two_right_sidebar',
  title: toolbarMessages.rightSidebar,
  icon: LayoutTwoColumnsSidebarRightIcon,
  iconFallback: LayoutTwoRightSidebarIcon
}, {
  id: 'editor.layout.twoLeftSidebar',
  type: 'two_left_sidebar',
  title: toolbarMessages.leftSidebar,
  icon: LayoutTwoColumnsSidebarLeftIcon,
  iconFallback: LayoutTwoLeftSidebarIcon
}, {
  id: 'editor.layout.threeWithSidebars',
  type: 'three_with_sidebars',
  title: toolbarMessages.threeColumnsWithSidebars,
  icon: LayoutThreeColumnsSidebarsIcon,
  iconFallback: LayoutThreeWithSidebarsIcon
}];

// These are used for advanced layout options
var LAYOUT_WITH_TWO_COL_DISTRIBUTION = [{
  id: 'editor.layout.twoEquals',
  type: 'two_equal',
  title: toolbarMessages.twoColumns,
  icon: LayoutTwoColumnsIcon,
  iconFallback: LayoutTwoEqualIcon
}, {
  id: 'editor.layout.twoRightSidebar',
  type: 'two_right_sidebar',
  title: toolbarMessages.rightSidebar,
  icon: LayoutTwoColumnsSidebarRightIcon,
  iconFallback: LayoutTwoRightSidebarIcon
}, {
  id: 'editor.layout.twoLeftSidebar',
  type: 'two_left_sidebar',
  title: toolbarMessages.leftSidebar,
  icon: LayoutTwoColumnsSidebarLeftIcon,
  iconFallback: LayoutTwoLeftSidebarIcon
}];
var LAYOUT_WITH_THREE_COL_DISTRIBUTION = [{
  id: 'editor.layout.threeEquals',
  type: 'three_equal',
  title: toolbarMessages.threeColumns,
  icon: LayoutThreeColumnsIcon,
  iconFallback: LayoutThreeEqualIcon
}, {
  id: 'editor.layout.threeWithSidebars',
  type: 'three_with_sidebars',
  title: toolbarMessages.threeColumnsWithSidebars,
  icon: LayoutThreeColumnsSidebarsIcon,
  iconFallback: LayoutThreeWithSidebarsIcon
}, {
  id: 'editor.layout.threeRightSidebars',
  type: 'three_right_sidebars',
  title: toolbarMessages.threeColumnsWithRightSidebars,
  icon: LayoutThreeWithRightSidebarsIcon,
  iconFallback: LayoutThreeWithRightSidebarsIcon
}, {
  id: 'editor.layout.threeLeftSidebars',
  type: 'three_left_sidebars',
  title: toolbarMessages.threeColumnsWithLeftSidebars,
  icon: LayoutThreeWithLeftSidebarsIcon,
  iconFallback: LayoutThreeWithLeftSidebarsIcon
}];
var buildLayoutButton = function buildLayoutButton(intl, item, currentLayout, editorAnalyticsAPI) {
  return {
    id: item.id,
    type: 'button',
    icon: item.icon,
    iconFallback: item.iconFallback,
    testId: item.title.id ? "".concat(item.title.id) : undefined,
    title: intl.formatMessage(item.title),
    onClick: setPresetLayout(editorAnalyticsAPI)(item.type),
    selected: !!currentLayout && currentLayout === item.type,
    tabIndex: null
  };
};
export var layoutToolbarTitle = 'Layout floating controls';
var iconPlaceholder = LayoutTwoColumnsIcon; // TODO: Replace with proper icon ED-25466

var getAdvancedLayoutItems = function getAdvancedLayoutItems(_ref) {
  var addSidebarLayouts = _ref.addSidebarLayouts,
    intl = _ref.intl,
    editorAnalyticsAPI = _ref.editorAnalyticsAPI,
    state = _ref.state,
    node = _ref.node,
    nodeType = _ref.nodeType,
    separator = _ref.separator,
    deleteButton = _ref.deleteButton,
    currentLayout = _ref.currentLayout;
  var numberOfColumns = node.content.childCount || 2;
  var distributionOptions = numberOfColumns === 2 ? LAYOUT_WITH_TWO_COL_DISTRIBUTION : numberOfColumns === 3 ? LAYOUT_WITH_THREE_COL_DISTRIBUTION : [];
  var columnOptions = [{
    title: intl.formatMessage(layoutMessages.columnOption, {
      count: 2
    }),
    //'2-columns',
    icon: iconPlaceholder,
    onClick: setPresetLayout(editorAnalyticsAPI)('two_equal'),
    selected: numberOfColumns === 2
  }, {
    title: intl.formatMessage(layoutMessages.columnOption, {
      count: 3
    }),
    //'3-columns'
    icon: iconPlaceholder,
    onClick: setPresetLayout(editorAnalyticsAPI)('three_equal'),
    selected: numberOfColumns === 3
  }, {
    title: intl.formatMessage(layoutMessages.columnOption, {
      count: 4
    }),
    //'4-columns'
    icon: iconPlaceholder,
    onClick: setPresetLayout(editorAnalyticsAPI)('four_equal'),
    selected: numberOfColumns === 4
  }, {
    title: intl.formatMessage(layoutMessages.columnOption, {
      count: 5
    }),
    //'5-columns'
    icon: iconPlaceholder,
    onClick: setPresetLayout(editorAnalyticsAPI)('five_equal'),
    selected: numberOfColumns === 5
  }];
  return [{
    type: 'dropdown',
    title: intl.formatMessage(layoutMessages.columnOption, {
      count: numberOfColumns
    }),
    //`${numberOfColumns}-columns`,
    options: columnOptions,
    showSelected: true,
    testId: 'column-options-button'
  }].concat(_toConsumableArray(distributionOptions.length > 0 ? [separator] : []), _toConsumableArray(addSidebarLayouts ? distributionOptions.map(function (i) {
    return buildLayoutButton(intl, i, currentLayout, editorAnalyticsAPI);
  }) : []), [separator, {
    type: 'copy-button',
    supportsViewMode: !fg('platform_editor_remove_copy_button_from_view_mode'),
    items: [{
      state: state,
      formatMessage: intl.formatMessage,
      nodeType: nodeType
    }]
  }, separator, deleteButton]);
};
export var buildToolbar = function buildToolbar(state, intl, pos, _allowBreakout, addSidebarLayouts, allowSingleColumnLayout, api) {
  var _api$decorations$acti, _api$decorations, _api$analytics;
  var _ref2 = (_api$decorations$acti = api === null || api === void 0 || (_api$decorations = api.decorations) === null || _api$decorations === void 0 ? void 0 : _api$decorations.actions) !== null && _api$decorations$acti !== void 0 ? _api$decorations$acti : {},
    hoverDecoration = _ref2.hoverDecoration;
  var editorAnalyticsAPI = api === null || api === void 0 || (_api$analytics = api.analytics) === null || _api$analytics === void 0 ? void 0 : _api$analytics.actions;
  var node = state.doc.nodeAt(pos);
  if (node) {
    var currentLayout = getPresetLayout(node);
    var separator = {
      type: 'separator'
    };
    var nodeType = state.schema.nodes.layoutSection;
    var deleteButton = {
      id: 'editor.layout.delete',
      type: 'button',
      appearance: 'danger',
      focusEditoronEnter: true,
      icon: DeleteIcon,
      iconFallback: RemoveIcon,
      testId: commonMessages.remove.id,
      title: intl.formatMessage(commonMessages.remove),
      onClick: deleteActiveLayoutNode(editorAnalyticsAPI),
      onMouseEnter: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(nodeType, true),
      onMouseLeave: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(nodeType, false),
      onFocus: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(nodeType, true),
      onBlur: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(nodeType, false),
      tabIndex: null
    };
    var layoutTypes = allowSingleColumnLayout ? LAYOUT_TYPES_WITH_SINGLE_COL : LAYOUT_TYPES;
    return {
      title: layoutToolbarTitle,
      // Ignored via go/ees005
      // eslint-disable-next-line @atlaskit/editor/no-as-casting
      getDomRef: function getDomRef(view) {
        return findDomRefAtPos(pos, view.domAtPos.bind(view));
      },
      nodeType: nodeType,
      groupLabel: intl.formatMessage(toolbarMessages.floatingToolbarRadioGroupAriaLabel),
      items: editorExperiment('advanced_layouts', true) ? getAdvancedLayoutItems({
        addSidebarLayouts: addSidebarLayouts,
        intl: intl,
        editorAnalyticsAPI: editorAnalyticsAPI,
        state: state,
        nodeType: nodeType,
        node: node,
        separator: separator,
        deleteButton: deleteButton,
        currentLayout: currentLayout
      }) : [].concat(_toConsumableArray(layoutTypes.map(function (i) {
        return buildLayoutButton(intl, i, currentLayout, editorAnalyticsAPI);
      })), _toConsumableArray(addSidebarLayouts ? SIDEBAR_LAYOUT_TYPES.map(function (i) {
        return buildLayoutButton(intl, i, currentLayout, editorAnalyticsAPI);
      }) : []), [separator, {
        type: 'copy-button',
        supportsViewMode: !fg('platform_editor_remove_copy_button_from_view_mode'),
        items: [{
          state: state,
          formatMessage: intl.formatMessage,
          nodeType: nodeType
        }]
      }, separator, deleteButton]),
      scrollable: true
    };
  }
  return;
};